import React, { useRef, useState } from 'react';
import {
  Badge, Box, Button, Collapse, HStack, IconButton, Skeleton, Spacer, Stack, Table, TableContainer,
  Tbody, Td, Text, Th, Thead, Tooltip, Tr, useColorModeValue, useToast,
} from '@chakra-ui/react';
import {
  FiUpload, FiDownload, FiTrash2, FiFile, FiFolderPlus, FiFolder, FiEdit2, FiCornerUpRight,
  FiChevronDown, FiChevronRight, FiPlus,
} from 'react-icons/fi';
import { NIL } from 'uuid';
import PageHeader from './PageHeader';
import AuroraBackground from './dashboard/AuroraBackground';
import GlassGridPanel from './GlassGridPanel';
import {
  StoredFileScope, useStoredFiles, useStoredFileActions, downloadStoredFile, StoredFileInfo,
} from '../hooks/useStoredFiles';
import useCurrentUser from '../hooks/useCurrentUser';
import StoredFilePreviewModal, { PreviewFile } from './StoredFilePreviewModal'; // item 935
import { usePrompt } from './ConfirmProvider'; // item 842 — Basix prompt (not window.prompt)

const fmtSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// item 129b — per-project document store. item 838 (Chad) — a TREE LEDGER: files organize into
// NAMED folders (StoredFile.Folder). Upload to the top level or into a folder; rename folders; move
// files between them. Reuses the in-DB StoredFile layer. Delete is Pulse Group admin only (item 142).
const ProjectDocumentsSection: React.FC<{
  projectId: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
  // item 382 — the ADMIN vault renders this same section with the admin-only scope
  scope?: StoredFileScope;
  title?: string;
  subtitleNote?: string;
  emptyText?: string;
  icon?: React.ReactNode;
  // item 839 — when set, this section shows ONLY that folder and files upload straight into it
  // (no folder chrome). Used to surface a single proposal's docs (folder = proposal number) on the
  // proposal edit page, sharing the SAME project store — the file also appears in the project tree.
  restrictToFolder?: string;
  // item 871 (Chad) — a READ-ONLY rendering: files list/download/preview only; no upload,
  // no folder chrome, no move/delete. The customer portal's "shared with you" section uses
  // it — customer UPLOADS live on each proposal, never in a standalone bucket.
  readOnly?: boolean;
}> = ({
  projectId, collapse, scope = StoredFileScope.ProjectDocument,
  title = 'Documents', subtitleNote, emptyText, icon, restrictToFolder, readOnly,
}) => {
  const enabled = projectId !== NIL;
  const { data, isLoading } = useStoredFiles(scope, projectId);
  const {
    upload, remove, move, renameFolder, isUploading,
  } = useStoredFileActions(scope, projectId);
  const { canModerate } = useCurrentUser();
  const prompt = usePrompt();
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef<string | undefined>(undefined); // folder to upload the next pick into
  const toast = useToast();
  const subColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  // item 935 (Jake) — in-app preview modal for any file in the tree
  const [preview, setPreview] = useState<PreviewFile | null>(null);
  const headBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const folderBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.100');
  const collapsed = collapse?.collapsed ?? false;
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  const toggleFolder = (key: string) => setOpenFolders((p) => ({ ...p, [key]: !p[key] }));

  const triggerUpload = (folder?: string) => {
    uploadTarget.current = folder;
    fileRef.current?.click();
  };

  // item 836c/838 — upload one or many files (any type) into the target folder (or top level).
  const onPick = async (files?: FileList | null) => {
    const folder = uploadTarget.current;
    uploadTarget.current = undefined;
    if (!files || files.length === 0) return;
    const picked = Array.from(files);
    let ok = 0;
    const failed: string[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const file of picked) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await upload(file, folder);
        ok += 1;
      } catch {
        failed.push(file.name);
      }
    }
    if (ok) toast({ status: 'success', title: `Uploaded ${ok} file${ok === 1 ? '' : 's'}${folder ? ` to ${folder}` : ''}` });
    if (failed.length) toast({ status: 'error', title: `Failed: ${failed.join(', ')}`, description: 'Try again.' });
  };

  const onNewFolder = async () => {
    const name = (await prompt('Name the folder:', { title: 'New folder', confirmLabel: 'Create', placeholder: 'e.g. PROP-100274' }))?.trim();
    if (!name) return;
    triggerUpload(name); // pick files to seed the folder (folders exist once they hold a file)
  };

  const onRenameFolder = async (folder: string) => {
    const next = (await prompt('Folder name:', { title: 'Rename folder', confirmLabel: 'Rename', defaultValue: folder }))?.trim();
    if (!next || next === folder) return;
    try {
      await renameFolder(folder, next);
      toast({ status: 'success', title: `Renamed to ${next}` });
    } catch {
      toast({ status: 'error', title: 'Rename failed' });
    }
  };

  const onMove = async (id: string, current?: string | null) => {
    const to = await prompt('Move to folder (blank = top level):', { title: 'Move file', confirmLabel: 'Move', defaultValue: current ?? '' });
    if (to === null) return;
    try {
      await move(id, to.trim() || null);
      toast({ status: 'success', title: to.trim() ? `Moved to ${to.trim()}` : 'Moved to top level' });
    } catch {
      toast({ status: 'error', title: 'Move failed' });
    }
  };

  const onDelete = async (id: string, name: string) => {
    try {
      await remove(id);
      toast({ status: 'success', title: `Deleted ${name}` });
    } catch (e: any) {
      toast({ status: 'error', title: 'Delete failed', description: e?.response?.status === 403 ? 'Only a Pulse Group admin can delete files.' : 'Try again.' });
    }
  };

  const files: StoredFileInfo[] = data ?? [];
  const count = data?.length;
  const folderNames = Array.from(new Set(files.map((f) => (f.folder || '').trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));
  const rootFiles = files.filter((f) => !(f.folder && f.folder.trim()));

  // item 839 — single-folder mode: show only this folder's files; uploads default into it.
  const restrictKey = (restrictToFolder || '').trim();
  const restricted = !!restrictKey;
  const restrictedFiles = restricted
    ? files.filter((f) => (f.folder || '').trim() === restrictKey)
    : files;
  const displayCount = restricted ? restrictedFiles.length : count;
  const subtitle = displayCount !== undefined
    ? `${displayCount} ${displayCount === 1 ? 'file' : 'files'}${!restricted && folderNames.length ? ` · ${folderNames.length} folder${folderNames.length === 1 ? '' : 's'}` : ''}${subtitleNote ? ` · ${subtitleNote}` : ''}`
    : subtitleNote;

  const fileTable = (rows: StoredFileInfo[]) => (
    <TableContainer>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr bg={headBg}>
            <Th>Name</Th>
            <Th>Uploaded by</Th>
            <Th isNumeric>Size</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((f) => (
            <Tr key={f.id}>
              {/* item 935 — click the name for the in-app preview */}
              <Td fontWeight="semibold" cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => setPreview({ id: f.id, name: f.name, contentType: f.contentType })}>{f.name}</Td>
              <Td>{f.createdByName ?? '—'}</Td>
              <Td isNumeric>{fmtSize(f.length)}</Td>
              <Td>
                <HStack spacing={1} justify="flex-end">
                  <Tooltip label="Download">
                    <IconButton size="sm" variant="ghost" aria-label="Download" icon={<FiDownload />} onClick={() => downloadStoredFile(f.id, f.name)} />
                  </Tooltip>
                  {!restricted && !readOnly && (
                    <Tooltip label="Move to folder">
                      <IconButton size="sm" variant="ghost" aria-label="Move" icon={<FiCornerUpRight />} onClick={() => onMove(f.id, f.folder)} />
                    </Tooltip>
                  )}
                  {canModerate && !readOnly ? (
                    <Tooltip label="Delete (Pulse Group admin)">
                      <IconButton size="sm" variant="ghost" colorScheme="red" aria-label="Delete" icon={<FiTrash2 />} onClick={() => onDelete(f.id, f.name)} />
                    </Tooltip>
                  ) : null}
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      px={{ base: 4, md: 10 }}
      pt={collapse?.collapsed ? 1 : { base: 2, md: 3 }}
      pb={collapse?.collapsed ? 1 : { base: 2, md: 3 }}
    >
      <AuroraBackground />
      <Box position="relative" zIndex={1}>
        <PageHeader
          dense
          onTitleClick={collapse?.onToggle}
          collapsed={collapsed}
          icon={icon ?? <FiFile />}
          title={title}
          subtitle={subtitle}
          actions={readOnly ? undefined : (
            <HStack spacing={2}>
              <input
                ref={fileRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => onPick(e.target.files)}
              />
              {!restricted && (
                <Button aria-label="New folder" size="xs" variant="outline" leftIcon={<FiFolderPlus />} isDisabled={!enabled || isUploading} onClick={onNewFolder}>
                  New folder
                </Button>
              )}
              <Button aria-label="Upload document" colorScheme="brandGreen" size="xs" minW="92px" leftIcon={<FiUpload />} isLoading={isUploading} isDisabled={!enabled} onClick={() => triggerUpload(restricted ? restrictKey : undefined)}>
                UPLOAD
              </Button>
            </HStack>
          )}
        />
        <Collapse in={!collapsed} animateOpacity unmountOnExit>
          <GlassGridPanel>
            {isLoading && (
              <Stack spacing={3}>{[1, 2, 3].map((k) => <Skeleton key={k} height="20px" borderRadius="md" />)}</Stack>
            )}
            {/* item 839 — single-folder (proposal) view: a flat table, no folder chrome */}
            {!isLoading && restricted && (
              restrictedFiles.length
                ? fileTable(restrictedFiles)
                : (
                  <Text fontSize="sm" color={subColor} py={6}>
                    {emptyText ?? 'No documents yet. Upload files here — they also appear in the project’s Documents tree.'}
                  </Text>
                )
            )}
            {!isLoading && !restricted && !data?.length && (
              <Text fontSize="sm" color={subColor} py={6}>
                {emptyText ?? 'No documents yet. Upload files, or create a folder to organize them.'}
              </Text>
            )}
            {!isLoading && !restricted && !!data?.length && (
              <Stack spacing={3}>
                {/* named folders (tree branches) */}
                {folderNames.map((folder) => {
                  const isOpen = openFolders[folder] !== false; // open by default
                  const inFolder = files.filter((f) => (f.folder || '').trim() === folder);
                  return (
                    <Box key={folder} borderWidth="1px" borderColor={headBg} borderRadius="md" overflow="hidden">
                      {/* item 838-UX (Chad) — Chakra 1.6 Flex ignores `gap`, so the header was
                          mashed together. Use HStack spacing + a Spacer + a count BADGE. */}
                      <HStack spacing={3} px={4} py={3} bg={folderBg} cursor="pointer" onClick={() => toggleFolder(folder)}>
                        <Box as={isOpen ? FiChevronDown : FiChevronRight} fontSize="16px" opacity={0.7} flexShrink={0} />
                        <Box as={FiFolder} fontSize="16px" flexShrink={0} />
                        <Text fontWeight="semibold" noOfLines={1}>{folder}</Text>
                        <Badge colorScheme="gray" borderRadius="full" px={2} flexShrink={0}>{inFolder.length}</Badge>
                        <Spacer />
                        {!readOnly && (
                          <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                            <Tooltip label="Add files to folder">
                              <IconButton size="sm" variant="ghost" aria-label="Add" icon={<FiPlus />} isDisabled={isUploading} onClick={() => triggerUpload(folder)} />
                            </Tooltip>
                            <Tooltip label="Rename folder">
                              <IconButton size="sm" variant="ghost" aria-label="Rename" icon={<FiEdit2 />} onClick={() => onRenameFolder(folder)} />
                            </Tooltip>
                          </HStack>
                        )}
                      </HStack>
                      <Collapse in={isOpen} animateOpacity>
                        {/* item 838-UX (Chad) — gap between the folder name row and the table header */}
                        <Box px={2} pt={3} pb={2}>{fileTable(inFolder)}</Box>
                      </Collapse>
                    </Box>
                  );
                })}
                {/* top-level (unfiled) files */}
                {rootFiles.length > 0 && (
                  folderNames.length > 0 ? (
                    <Box>
                      <Text fontSize="xs" textTransform="uppercase" color={subColor} px={1} mb={1}>Top level</Text>
                      {fileTable(rootFiles)}
                    </Box>
                  ) : fileTable(rootFiles)
                )}
              </Stack>
            )}
          </GlassGridPanel>
        </Collapse>
      </Box>
      {/* item 935 — in-app preview (download available inside) */}
      <StoredFilePreviewModal file={preview} onClose={() => setPreview(null)} />
    </Box>
  );
};

ProjectDocumentsSection.defaultProps = {
  scope: StoredFileScope.ProjectDocument,
  title: 'Documents',
  readOnly: false,
  subtitleNote: undefined,
  emptyText: undefined,
  icon: undefined,
  collapse: undefined,
  restrictToFolder: undefined,
};

export default ProjectDocumentsSection;
